import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function StoreCheckout() {
    const navigate = useNavigate();
    const { items, updateQty, removeItem, subtotalCents } = useCart();
    const [loading, setLoading] = useState(false);
    const [promo, setPromo] = useState("");
    const [discount, setDiscount] = useState(null); // {code, discount_cents}
    const [applying, setApplying] = useState(false);
    const [form, setForm] = useState({
        buyer_name: "", buyer_email: "", buyer_phone: "",
        line1: "", line2: "", city: "", state: "", postal_code: "", country: "US",
    });
    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const shippingCents = items.length === 0 ? 0 : (subtotalCents >= 10000 ? 0 : 999);
    const discountCents = discount?.discount_cents || 0;
    const totalCents = Math.max(0, subtotalCents - discountCents) + shippingCents;

    const applyPromo = async () => {
        if (!promo.trim()) return;
        setApplying(true);
        try {
            const { data } = await api.post("/discount-codes/validate", {
                code: promo.trim(), subtotal_cents: subtotalCents, applies_to: "store",
            });
            setDiscount(data);
            toast.success(`Code applied: -${(data.discount_cents / 100).toFixed(2)}`);
        } catch (err) {
            setDiscount(null);
            toast.error(formatErr(err));
        } finally {
            setApplying(false);
        }
    };

    const removePromo = () => { setPromo(""); setDiscount(null); };

    const submit = async (e) => {
        e.preventDefault();
        const required = ["buyer_name", "buyer_email", "line1", "city", "state", "postal_code"];
        for (const k of required) {
            if (!form[k]?.trim()) { toast.error("Please complete all required fields."); return; }
        }
        if (!items.length) { toast.error("Cart is empty."); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/store/checkout", {
                items: items.map((i) => ({
                    product_id: i.product_id, product_name: i.product_name,
                    image_url: i.image_url, size: i.size,
                    quantity: i.quantity, unit_price_cents: i.unit_price_cents,
                })),
                buyer_name: form.buyer_name,
                buyer_email: form.buyer_email,
                buyer_phone: form.buyer_phone || null,
                shipping: {
                    line1: form.line1, line2: form.line2 || "",
                    city: form.city, state: form.state,
                    postal_code: form.postal_code, country: form.country,
                },
                discount_code: discount?.code || null,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-5 md:px-10 py-24 text-center" data-testid="cart-empty">
                <ShoppingBag className="mx-auto text-gold/60" size={48} />
                <h1 className="h-display text-4xl mt-5">Your cart is empty</h1>
                <Link to="/store" className="inline-flex items-center gap-2 mt-6 btn-gold px-6 py-3 text-xs">
                    <ArrowLeft size={14} /> Back to Store
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page" className="grain max-w-6xl mx-auto px-5 md:px-10 py-12">
            <Link to="/store" className="inline-flex items-center gap-2 text-ink hover:text-gold text-xs tracking-[0.3em] uppercase font-bold mb-8">
                <ArrowLeft size={14} /> Continue Shopping
            </Link>
            <h1 className="h-display text-5xl mb-10">Checkout</h1>

            <div className="grid lg:grid-cols-5 gap-8">
                <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="checkout-form">
                    <h2 className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold">Contact</h2>
                    <div className="grid md:grid-cols-2 gap-3">
                        <Field label="Full Name *" value={form.buyer_name} onChange={onChange("buyer_name")} testid="co-name" required />
                        <Field label="Email *" type="email" value={form.buyer_email} onChange={onChange("buyer_email")} testid="co-email" required />
                        <Field label="Phone" value={form.buyer_phone} onChange={onChange("buyer_phone")} testid="co-phone" />
                    </div>

                    <h2 className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mt-6">Shipping Address</h2>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="md:col-span-2"><Field label="Street Address *" value={form.line1} onChange={onChange("line1")} testid="co-line1" required /></div>
                        <div className="md:col-span-2"><Field label="Apt / Suite (optional)" value={form.line2} onChange={onChange("line2")} testid="co-line2" /></div>
                        <Field label="City *" value={form.city} onChange={onChange("city")} testid="co-city" required />
                        <Field label="State *" value={form.state} onChange={onChange("state")} testid="co-state" required />
                        <Field label="ZIP *" value={form.postal_code} onChange={onChange("postal_code")} testid="co-zip" required />
                        <Field label="Country" value={form.country} onChange={onChange("country")} testid="co-country" />
                    </div>

                    <button type="submit" disabled={loading} data-testid="co-submit" className="btn-gold w-full py-4 text-sm mt-6">
                        {loading ? "Redirecting to Stripe…" : `Pay ${fmt(totalCents)} with Stripe →`}
                    </button>
                    <p className="text-ink text-xs">Secure checkout via Stripe. Test mode active until live key is set.</p>
                </form>

                <div className="lg:col-span-2 space-y-3" data-testid="cart-summary">
                    {items.map((i) => (
                        <div key={`${i.product_id}-${i.size || ""}`} className="card-flat p-4 flex gap-4 items-center">
                            <div className="w-16 h-16 bg-bg-soft bg-cover bg-center shrink-0" style={{ backgroundImage: `url('${i.image_url}')` }} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold truncate">{i.product_name}</div>
                                {i.size && <div className="text-xs text-ink mt-1">Size: {i.size}</div>}
                                <div className="flex items-center gap-2 mt-2">
                                    <button type="button" onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="text-ink hover:text-gold p-1" aria-label="decrease"><Minus size={12} /></button>
                                    <span className="text-xs font-bold">{i.quantity}</span>
                                    <button type="button" onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="text-ink hover:text-gold p-1" aria-label="increase"><Plus size={12} /></button>
                                    <button type="button" onClick={() => removeItem(i.product_id, i.size)} className="ml-auto text-ink hover:text-red-400 p-1" aria-label="remove"><Trash2 size={12} /></button>
                                </div>
                            </div>
                            <div className="text-sm font-bold">{fmt(i.unit_price_cents * i.quantity)}</div>
                        </div>
                    ))}
                    <div className="card-flat p-4 space-y-2" data-testid="cart-totals">
                        <Row label="Subtotal" value={fmt(subtotalCents)} />
                        {discount && (
                            <Row label={`Discount (${discount.code})`} value={`-${fmt(discountCents)}`} testid="row-discount" />
                        )}
                        <Row label="Shipping" value={shippingCents === 0 ? "Free" : fmt(shippingCents)} />
                        <div className="border-t border-line pt-2 mt-2">
                            <Row label="Total" value={fmt(totalCents)} bold />
                        </div>

                        <div className="border-t border-line pt-3 mt-3">
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">Promo Code</label>
                            {discount ? (
                                <div className="flex items-center gap-2">
                                    <span data-testid="promo-applied" className="text-gold text-xs tracking-widest font-bold uppercase">✓ {discount.code}</span>
                                    <button type="button" onClick={removePromo} className="ml-auto text-ink text-xs hover:text-red-400">Remove</button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={promo}
                                        onChange={(e) => setPromo(e.target.value.toUpperCase())}
                                        placeholder="KINGS10"
                                        data-testid="promo-input"
                                        className="flex-1 px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none uppercase tracking-widest"
                                    />
                                    <button type="button" onClick={applyPromo} disabled={applying || !promo.trim()} data-testid="promo-apply" className="btn-ghost-gold px-3 py-2 text-xs">
                                        {applying ? "…" : "Apply"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, testid, type = "text", required, value, onChange }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">{label}</label>
            <input type={type} required={required} value={value} onChange={onChange} data-testid={testid}
                className="w-full px-4 py-3 text-sm bg-bg-soft border border-line focus:border-gold outline-none transition-colors" />
        </div>
    );
}
function Row({ label, value, bold, testid }) {
    return (
        <div className="flex justify-between text-sm" data-testid={testid}>
            <span className={bold ? "font-bold" : "text-ink"}>{label}</span>
            <span className={bold ? "font-bold text-gold" : ""}>{value}</span>
        </div>
    );
}
